/**
 * The platform console's API client.
 *
 * A separate token, under a separate storage key, from a separate sign-in.
 * Sharing either with the club client would mean a browser signed in to Club
 * Office was also signed in to the console that reads every club — which is
 * exactly the confusion the server avoids by signing the two kinds of token
 * with different issuers.
 */
const BASE = (import.meta.env.VITE_API_BASE ?? '') + '/api/platform';
const TOKEN_KEY = 'lcc.platform.token';

export const platformToken = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (v: string) => localStorage.setItem(TOKEN_KEY, v),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export class PlatformError extends Error {
  constructor(readonly status: number, message: string, readonly details?: unknown) {
    super(message);
    this.name = 'PlatformError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const jwt = platformToken.get();
  if (jwt) headers.authorization = `Bearer ${jwt}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(BASE + path, {
    method, headers, body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const err = (payload as { error?: { message?: string; details?: unknown } }).error;
    // An expired console session should land on its own sign-in, not the club's.
    if (res.status === 401) platformToken.clear();
    throw new PlatformError(res.status, err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return payload as T;
}

export const platformApi = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
};

export interface PlatformClub {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  isActive: boolean;
  plan: string;
  memberCount: number;
  grossCents: number;
  commissionCents: number;
  lastPaymentAt: string | null;
  onboardedAt: string | null;
  stripe: {
    accountId: string | null;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
  };
  commission: {
    enabled: boolean;
    percent: number;
    fixedCents: number;
    minCents: number;
    capCents: number | null;
  };
}

export interface PlatformRevenue {
  totals: {
    payingClubs: number;
    grossCents: number;
    commissionCents: number;
    payments: number;
    inPersonPayments: number;
  };
  byMonth: Array<{ month: string; grossCents: number; commissionCents: number; payments: number }>;
  byClub: Array<{
    id: string; name: string; slug: string;
    grossCents: number; commissionCents: number; payments: number;
  }>;
}
